import type { MaterialCategory, StockItem, StockMovement, StockStatus } from './types.js';

/**
 * Malzeme ve stok ekranlarının saf yardımcıları (M12a): süzgeçler, envanter ağacı, dağılım,
 * özet ve uyarılar. Durum (normal/low/critical) sunucuda `@felix/domain` kuralıyla hesaplanır;
 * burada yalnız sayılır.
 */

/** Ana kategoriler, kategori sırasıyla, tekrarsız. */
export function mainCategories(categories: readonly MaterialCategory[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const category of [...categories].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.mainCategory.localeCompare(b.mainCategory),
  )) {
    if (seen.has(category.mainCategory)) continue;
    seen.add(category.mainCategory);
    result.push(category.mainCategory);
  }
  return result;
}

/** Ana kategorinin alt kategorileri; ana kategori yoksa hepsi. */
export function subCategories(
  categories: readonly MaterialCategory[],
  mainCategory: string | null,
): MaterialCategory[] {
  return categories.filter((c) => mainCategory === null || c.mainCategory === mainCategory);
}

export interface StockDistribution {
  total: number;
  normal: number;
  low: number;
  critical: number;
}

/** Stok seviye dağılımı ("Stock Level Distribution"). */
export function stockDistribution(items: readonly StockItem[]): StockDistribution {
  const count = (status: StockStatus) => items.filter((i) => i.status === status).length;
  return {
    total: items.length,
    normal: count('normal'),
    low: count('low'),
    critical: count('critical'),
  };
}

/** Yüzde, tam sayıya yuvarlanmış; toplam sıfırsa sıfır. */
export function percent(part: number, total: number): number {
  return total === 0 ? 0 : Math.round((part / total) * 100);
}

export interface TreeNode {
  mainCategory: string;
  count: number;
  children: { categoryId: string; subCategory: string; count: number }[];
}

/** Envanter ağacı ("Inventory Tree"): stok kalemlerinin ana ve alt kategorileri, sayılarıyla. */
export function inventoryTree(items: readonly StockItem[]): TreeNode[] {
  const nodes = new Map<string, TreeNode>();
  for (const item of items) {
    const node = nodes.get(item.mainCategory) ?? {
      mainCategory: item.mainCategory,
      count: 0,
      children: [],
    };
    node.count += 1;
    const child = node.children.find((c) => c.categoryId === item.categoryId);
    if (child) child.count += 1;
    else
      node.children.push({ categoryId: item.categoryId, subCategory: item.subCategory, count: 1 });
    nodes.set(item.mainCategory, node);
  }
  return [...nodes.values()];
}

export interface StockFilter {
  /** Ad, özellik, şirket kodu, IMPA ya da ISSA. */
  q?: string;
  mainCategory?: string | null;
  categoryId?: string | null;
  /** IMPA ya da ISSA kodunun başı. */
  code?: string;
  location?: string | null;
  status?: StockStatus | null;
}

const fold = (text: string | null | undefined) => (text ?? '').toLocaleLowerCase('en');

/** Stok kalemlerini süzer; sıra korunur. */
export function filterStockItems(items: readonly StockItem[], filter: StockFilter): StockItem[] {
  const q = fold(filter.q?.trim());
  const code = fold(filter.code?.trim());
  return items.filter(
    (item) =>
      (!q ||
        [item.name, item.specification, item.companyCode, item.impaCode, item.issaCode].some((v) =>
          fold(v).includes(q),
        )) &&
      (!code || fold(item.impaCode).startsWith(code) || fold(item.issaCode).startsWith(code)) &&
      (!filter.mainCategory || item.mainCategory === filter.mainCategory) &&
      (!filter.categoryId || item.categoryId === filter.categoryId) &&
      (!filter.location || item.location === filter.location) &&
      (!filter.status || item.status === filter.status),
  );
}

/** Stok yerleri, alfabetik, tekrarsız; yeri boş kalem listede yok. */
export function stockLocations(items: readonly StockItem[]): string[] {
  return [...new Set(items.flatMap((i) => (i.location ? [i.location] : [])))].sort((a, b) =>
    a.localeCompare(b),
  );
}

export interface SummaryRow {
  key: string;
  items: number;
  low: number;
  critical: number;
}

/** Stok özeti ("Stock Summary"): kategoriye ya da yere göre kalem, düşük ve kritik sayıları. */
export function stockSummary(
  items: readonly StockItem[],
  by: 'category' | 'location',
): { rows: SummaryRow[]; total: SummaryRow } {
  const rows = new Map<string, SummaryRow>();
  for (const item of items) {
    const key = by === 'category' ? item.mainCategory : (item.location ?? '');
    const row = rows.get(key) ?? { key, items: 0, low: 0, critical: 0 };
    row.items += 1;
    if (item.status === 'low') row.low += 1;
    if (item.status === 'critical') row.critical += 1;
    rows.set(key, row);
  }
  const list = [...rows.values()];
  return {
    rows: list,
    total: {
      key: '',
      items: list.reduce((sum, r) => sum + r.items, 0),
      low: list.reduce((sum, r) => sum + r.low, 0),
      critical: list.reduce((sum, r) => sum + r.critical, 0),
    },
  };
}

/** Uyarılar: kritik önce, sonra düşük; aynı durumda eldeki/en az oranı küçük olan önce. */
export function stockAlerts(items: readonly StockItem[]): StockItem[] {
  const rank = (status: StockStatus) => (status === 'critical' ? 0 : status === 'low' ? 1 : 2);
  const ratio = (item: StockItem) => (item.minQty > 0 ? item.onHand / item.minQty : item.onHand);
  return items
    .filter((item) => item.status !== 'normal')
    .sort((a, b) => rank(a.status) - rank(b.status) || ratio(a) - ratio(b));
}

/** Hareket sekmeleri: giriş (giriş, mal kabulü, artı düzeltme), çıkış (çıkış, eksi düzeltme). */
export function movementsOf(
  movements: readonly StockMovement[],
  tab: 'in' | 'out' | 'history',
): StockMovement[] {
  if (tab === 'history') return [...movements];
  return movements.filter((m) => (tab === 'in' ? m.quantity > 0 : m.quantity < 0));
}

/** Formdaki pozitif miktarı hareketin işaretli miktarına çevirir (ADR 0026 §2). */
export function signedQuantity(kind: 'in' | 'out' | 'adjust', quantity: number): number {
  if (kind === 'in') return Math.abs(quantity);
  if (kind === 'out') return -Math.abs(quantity);
  return quantity;
}

/** Miktar, dile göre; ondalık en çok üç hane. */
export function formatQuantity(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(value);
}

/** Fiyat ve değer: iki ondalık ve para birimi; bilinmiyorsa tire (ADR 0047 §5). */
export function formatMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
  locale: string,
): string {
  if (value === null || value === undefined || !currency) return '—';
  const amount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `${amount} ${currency}`;
}

/** Hareketin gemi yerel saati (`SS:DD`): UTC an + gemi saati farkı (CLAUDE.md "Zaman"). */
export function localClock(iso: string, tzOffsetMin: number): string {
  return new Date(Date.parse(iso) + tzOffsetMin * 60_000).toISOString().slice(11, 16);
}

export type StockSort = 'category' | 'name' | 'onHand' | 'status';
export type SortDirection = 'asc' | 'desc';

/**
 * Tablonun sırası: kategori (sunucunun sırası), ad, eldeki (azdan çoğa) ya da durum (kritik
 * önce); `desc` tersi. Eşitlikte sunucunun sırası korunur. Kategori tersinde ana kategori
 * grupları ters dizilir, grup içi sunucunun sırasıdır (M6f G5-26).
 */
export function sortStockItems(
  items: readonly StockItem[],
  sort: StockSort,
  locale: string,
  direction: SortDirection = 'asc',
): StockItem[] {
  const sign = direction === 'asc' ? 1 : -1;
  if (sort === 'category') {
    if (direction === 'asc') return [...items];
    const groups = new Map<string, number>();
    for (const item of items) {
      if (!groups.has(item.mainCategory)) groups.set(item.mainCategory, groups.size);
    }
    const rankOf = (item: StockItem) => groups.get(item.mainCategory) ?? 0;
    return [...items].sort((a, b) => rankOf(b) - rankOf(a));
  }
  const rank = (status: StockStatus) => (status === 'critical' ? 0 : status === 'low' ? 1 : 2);
  return [...items].sort(
    (a, b) =>
      sign *
      (sort === 'name'
        ? a.name.localeCompare(b.name, locale)
        : sort === 'onHand'
          ? a.onHand - b.onHand
          : rank(a.status) - rank(b.status)),
  );
}

/** Birim kodunun ekrandaki adı (`gemi-envanter-kumanya.jpeg`: Ltr, Kg, Pcs); kod saklandığı gibi kalır. */
const UNIT_LABELS: Readonly<Record<string, string>> = { MTR: 'm' };

export function formatUnit(code: string): string {
  if (!code) return '';
  return UNIT_LABELS[code] ?? code.charAt(0).toUpperCase() + code.slice(1).toLowerCase();
}

/** İşaretli miktar: artı `+`, eksi U+2212 "−" (plan §0.6 "Sayılar"). */
export function formatSignedQuantity(value: number, locale: string): string {
  const text = formatQuantity(Math.abs(value), locale);
  return value > 0 ? `+${text}` : value < 0 ? `\u2212${text}` : text;
}
